import React from "react"

import { Calendar, MapPin, MessageSquare, Route as RouteIcon, Timer, User } from "lucide-react"

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import type { Shift } from "@/services/shiftsService"
import { formatTo24HourTime } from "@/utils/timeFormat"

import { getShiftTypeIcon } from "./shiftTypeIcons"

type DisplayStatus =
  | "planned"
  | "empty"
  | "open"
  | "in-progress"
  | "clocked-in"
  | "clocked-out"
  | "overdue"
  | "no-show"
  | "cancelled"
  | "unnotified-absence"

type ShiftWithDisplay = Shift & {
  displayStatus: DisplayStatus
}

interface ShiftBlockProps {
  shift: ShiftWithDisplay
  clockInLocation?: string | null
  clockOutLocation?: string | null
  primaryDriverName?: string | null
  onDragStart?: () => void
  onDragEnd?: () => void
  onClick?: () => void
}

const ShiftBlock: React.FC<ShiftBlockProps> = ({
  shift,
  clockInLocation = null,
  clockOutLocation = null,
  primaryDriverName = null,
  onDragStart,
  onDragEnd,
  onClick,
}) => {
  const getTypeIcon = (type: string) => {
    const TypeIcon = getShiftTypeIcon(type)
    return <TypeIcon className="h-3 w-3" />
  }

  const getStatusColor = (status: DisplayStatus) => {
    switch (status) {
      case "planned":
        return "bg-white border border-palette-jungleGreen200 text-palette-jungleGreen800"
      case "in-progress":
        return "bg-green-100 border border-green-300 text-green-800"
      case "empty":
        return "bg-blue-100 border border-blue-200 text-blue-800"
      case "open":
        return "bg-palette-jungleGreen100 border border-palette-jungleGreen200 text-palette-jungleGreen800"
      case "clocked-in":
        return "bg-green-300 border border-green-500 text-green-900"
      case "clocked-out":
        return "bg-green-50 border border-green-200 text-green-800"
      case "overdue":
        return "bg-orange-100 border border-orange-300 text-orange-800"
      case "no-show":
        return "bg-red-100 border border-red-300 text-red-800"
      case "cancelled":
        return "bg-purple-100 border border-purple-300 text-purple-800"
      case "unnotified-absence":
        return "bg-amber-100 border border-amber-300 text-amber-800"
      default:
        return "bg-white border border-palette-jungleGreen200 text-palette-jungleGreen800"
    }
  }

  const personName = shift.people?.person || "Unassigned"
  const primaryPersonId = shift.people?.primaryPersonId ?? null
  const notes = shift.notes?.trim()
  const startTimeFormatted = formatTo24HourTime(shift.startTime)
  const endTimeFormatted = formatTo24HourTime(shift.endTime)
  const clockInTimeFormatted = formatTo24HourTime(shift.timesheet?.clockedInTime) ?? null
  const clockOutTimeFormatted = formatTo24HourTime(shift.timesheet?.clockedOutTime) ?? null
  const hasBothClockLocations = Boolean(clockInLocation && clockOutLocation)
  const clockLocationsInlineText =
    clockInLocation && clockOutLocation
      ? `${clockInLocation} - ${clockOutLocation}`
      : clockInLocation
        ? `${clockInLocation}`
        : clockOutLocation
          ? `${clockOutLocation}`
          : ""

  return (
    <div
      className={`p-3 rounded-lg ${getStatusColor(
        shift.displayStatus,
      )} cursor-pointer hover:shadow-sm transition-shadow min-h-24 flex flex-col`}
      draggable={!!onDragStart}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      {/* Header with Person name */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-sm truncate">{personName}</h3>
      </div>
      {primaryPersonId && (
        <div className="text-xs text-palette-jungleGreen500 truncate">
          Primary: {primaryDriverName ?? "Unknown"}
        </div>
      )}
      {shift.preassignedRoute && (
        <div className="flex items-center gap-2 text-xs mt-1">
          <RouteIcon className="h-3 w-3" />
          <span className="truncate">{shift.preassignedRoute}</span>
        </div>
      )}

      {/* Start - End Times */}
      <div className="flex items-center gap-2 text-xs mt-1">
        <Calendar className="h-3 w-3" />
        <span className="truncate">
          {startTimeFormatted} - {endTimeFormatted}
        </span>
      </div>

      {/* Actual clock times */}
      {(clockInTimeFormatted || clockOutTimeFormatted) && (
        <div className="text-xs">
          <div className="flex items-center gap-2">
            <Timer className="h-3 w-3" />
            <span>
              {clockInTimeFormatted || "--:--"} - {clockOutTimeFormatted || "--:--"}
            </span>
          </div>
          {clockLocationsInlineText && (
            <div className="mt-0.5 text-xs">
              {hasBothClockLocations ? (
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <div className="flex max-w-full min-w-0 items-center gap-2 cursor-help">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{clockLocationsInlineText}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm whitespace-pre-wrap break-words">
                    {clockLocationsInlineText}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div className="flex items-center gap-2">
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span className="break-words">{clockLocationsInlineText}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Type */}
      <div className="flex items-center gap-2 text-xs">
        {getTypeIcon(shift.type)}
        <span className="truncate">{shift.type}</span>
      </div>

      {/* Employer */}
      <div className="flex items-center gap-2 text-xs">
        <User className="h-3 w-3" />
        <span className="truncate">{shift.employer}</span>
      </div>
      {notes && (
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <div className="mt-auto flex items-center gap-2 text-xs pt-1 cursor-help">
              <MessageSquare className="h-3 w-3 shrink-0" />
              <span className="truncate">{notes}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs whitespace-pre-wrap break-words">
            {notes}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

export default ShiftBlock
